import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import parse from 'html-react-parser';
import { Link } from 'react-router-dom';

const VideoPlayer = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [currentVideo, setCurrentVideo] = useState({});
  const [iframeLoaded, setIframeLoaded] = useState(false);

  // Fetch videos (with session storage caching)
  const fetchVideos = useCallback(async () => {
    try {
      setLoading(true);

      // Check for cached videos in session storage
      const cachedVideos = sessionStorage.getItem('videos');
      if (cachedVideos) {
        const parsedVideos = JSON.parse(cachedVideos);
        setVideos(parsedVideos);
        setCurrentVideo(parsedVideos[0] || {});
        setLoading(false);
        return; // Exit early since cached data is used
      }

      const response = await axios.get('/api/video');
      const content = response.data;
      sessionStorage.setItem('videos', JSON.stringify(content));

      setVideos(content);
      setCurrentVideo(content[0] || {});
      setLoading(false);
    } catch (error) {
      //console.error(`Error fetching video content: ${error}`);
      setError(true);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  // When the current video changes, reset the iframeLoaded flag so that the user sees the facade again
  useEffect(() => {
    setIframeLoaded(false);
  }, [currentVideo]);

  // When the user clicks the facade, show the actual YouTube embed
  const loadIframe = () => {
    setIframeLoaded(true);
  };

  if (error) {
    return <p>Error loading videos. Please try again later.</p>;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 xl:gap-6">
      {/* Video title and summary section */}
      <div className="col-span-1 order-1 md:col-span-1 md:order-2">
        <Link
          to={`${currentVideo.pageUrl}`}
          style={{ textDecoration: 'none', color: 'inherit' }}
        >
          <h2
            className="mb-4 font-semibold"
            style={{
              fontFamily: 'Arial, Helvetica Neue, Helvetica, sans-serif',
              fontSize: '27px',
              lineHeight: '1.35em',
              fontWeight: '500',
            }}
          >
            {currentVideo.title}
          </h2>
          <p
            className="mb-4"
            style={{
              fontFamily: 'var(--font-paragraph)',
              fontWeight: '400',
              fontSize: '18px',
              lineHeight: '1.56em',
            }}
          >
            {currentVideo.summary ? parse(currentVideo.summary) : null}
          </p>
        </Link>
      </div>

      {/* Video container section */}
      <div className="video-container col-span-1 order-1 md:col-start-2 md:col-span-2 md:order-2">
        {iframeLoaded ? ( 
          // If the iframe is loaded, parse and render the embed code.
          currentVideo.videoEmbed ? (
            parse(
              currentVideo.videoEmbed
                .replace(/width="\d+"/, 'width="100%"')
                .replace(/height="\d+"/, 'height="100%"')
                .replace(/src="([^"]+)"/, (match, src) => {
                  const delimiter = src.includes('?') ? '&' : '?';
                  if (!/autoplay=1/.test(src)) {
                    src = `${src}${delimiter}autoplay=1`;
                  }
                  return `src="${src}"`;
                })
            )
          ) : null
        ) : (

          // Otherwise, display a clickable thumbnail facade with a play button overlay.
          <div
            onClick={loadIframe}
            style={{ cursor: 'pointer', position: 'relative' }}
          >
            <img
              src={currentVideo.thumbnail}
              alt={currentVideo.title}
              style={{ width: '100%' }}
            />
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                fontSize: '3rem',
                color: '#fff',
                textShadow: '0 0 10px rgba(0,0,0,0.5)',
              }}
            >
              ▶
            </div>
          </div>
        )}
      </div>

      {/* Thumbnails list section */}
      <div className="md:col-span-1 order-3 space-y-2 md:space-y-0 overflow-y-auto max-h-3-thumbnails">
        {videos.slice(0, 5).map((video) => (
          <div
            key={video.id}
            onClick={() => setCurrentVideo(video)}
            className="cursor-pointer grid grid-cols-2 gap-4 items-center"
          >
            <img
              src={video.thumbnail}
              alt={video.title}
              className="w-full max-h-20"
            />
            <p
              className="mb-4 font-semibold"
              style={{
                fontFamily:
                  'Arial, Helvetica Neue, Helvetica, sans-serif',
                fontSize: '18px',
                lineHeight: '1.35em',
                fontWeight: '500',
              }}
            >
              {video.title}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default VideoPlayer;
